import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getSessionId,
  clearLocalSession,
  touchActivity,
  isSessionTimedOut,
  buildLoginRedirect,
} from '../utils/sessionUtils';
import { isPublicPath } from '../utils/publicPaths';

const CHECK_INTERVAL_MS = 60 * 1000; // check every 60 seconds

/**
 * Hook that enforces a 60-minute inactivity timeout.
 *  - Listens for user interactions and updates lastActivity timestamp.
 *  - Checks every minute whether the session has timed out.
 *  - On timeout: clears auth data, shows a toast, and redirects to
 *    /login?returnTo=<current path>.
 *
 * Only active when the user has a session (sessionId in localStorage).
 */
export default function useSessionTimeout() {
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    // Only run for logged-in users
    if (!getSessionId()) return;

    // Seed activity timestamp on mount (login / page load)
    touchActivity();

    // ── User interaction listeners ──────────────────────────────────
    const events = ['click', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    // Throttle: update at most once every 30 seconds to avoid perf cost
    let lastTouch = 0;
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastTouch > 30_000) {
        lastTouch = now;
        touchActivity();
      }
    };

    events.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));

    // ── Periodic timeout check ──────────────────────────────────────
    const checkTimeout = () => {
      if (!getSessionId()) return; // already logged out
      // Never force a redirect away from a public page (marketing, legal,
      // public event/ticket pages). /about is not a tenant surface — an
      // idle visitor there must not be bounced to /login?returnTo=%2Fabout.
      if (isPublicPath(window.location.pathname)) return;
      if (isSessionTimedOut()) {
        clearLocalSession();
        toast.info('Your session has expired due to inactivity. Please log in again.', {
          autoClose: 5000,
        });
        navigate(buildLoginRedirect(), { replace: true });
      }
    };

    timerRef.current = setInterval(checkTimeout, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(timerRef.current);
      events.forEach((evt) => window.removeEventListener(evt, handleActivity));
    };
  }, [navigate]);
}
